import CardBase from '@components/design-system/CardBase'
import { CountdownFooterCard } from '@components/shared/cards/tags/CountdownFooterCard'
import Icon, { CardTypes, SaleTypesIcons } from '@components/shared/Icon'
import { chainConfig } from '@config'
import { formatToLocaleString, networkNameById } from '@services/UtilService'
import { Progress } from 'antd'
import { now } from 'moment'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { CrowdPad, CrowdPadStatus } from '../../../types/CrowdPadTypes'

export interface CrowdpadCardProps {
  crowdpadItem: CrowdPad
}

interface PriceAndTitleNft {
  title: string
  value: string
  symbol: string
  statusBar: string
  type: string
  icon: SaleTypesIcons | CardTypes
}

export default function CrowdpadCard({ crowdpadItem }: CrowdpadCardProps) {
  const [nftPriceType, setNftPriceType] = useState<PriceAndTitleNft | undefined>(undefined)
  const currentPoolProgress = formatToLocaleString((Number(crowdpadItem.amount) / Number(crowdpadItem.reservePrice)) * 100, 2)
  const config = chainConfig(crowdpadItem.chainId)

  useEffect(() => {
    const getSymbol = (): string => {
      return crowdpadItem.paymentToken.symbol === null
        ? config?.networkTokenSymbol.toUpperCase() || ''
        : crowdpadItem.paymentToken.symbol.toUpperCase()
    }

    const getPrice = (price: string | null): string => {
      if (price === null) {
        return 'Not available'
      }
      return formatToLocaleString(price.replaceAll(',', ''), 2)
    }

    const getTitle = (): PriceAndTitleNft => {
      if (crowdpadItem.status === CrowdPadStatus.CREATED || crowdpadItem.status === CrowdPadStatus.FUNDED) {
        return {
          title: 'Target Price',
          value: getPrice(crowdpadItem.reservePrice),
          symbol: getSymbol(),
          statusBar: 'Pool Progress',
          type: 'poolProgress',
          icon: CardTypes.poolProgress
        }
      }

      if (crowdpadItem.status === CrowdPadStatus.STARTING_OR_ENDING && crowdpadItem.cutoff && now() / 1000 > crowdpadItem.cutoff) {
        return {
          title: 'Sold for',
          value: getPrice(crowdpadItem.reservePrice),
          symbol: getSymbol(),
          statusBar: 'NFT Sold',
          type: 'recentlySold',
          icon: SaleTypesIcons.recentlySold
        }
      }

      if (crowdpadItem.status === CrowdPadStatus.STARTING_OR_ENDING && crowdpadItem.cutoff && now() / 1000 < crowdpadItem.cutoff) {
        return {
          title: 'Accumulated',
          value: `${formatToLocaleString(crowdpadItem.reservePrice, 2)}` ?? '',
          symbol: getSymbol(),
          statusBar: 'Pool ends in',
          type: 'liveAuction',
          icon: SaleTypesIcons.liveAuction
        }
      }

      return {
        title: 'Price',
        value: getPrice(crowdpadItem.reservePrice),
        symbol: getSymbol(),
        statusBar: 'Buy Now',
        type: 'buyNow',
        icon: SaleTypesIcons.buyNow
      }
    }
    setNftPriceType(getTitle())
  }, [crowdpadItem, config?.networkTokenSymbol])

  return (
    <CardBase
      totalMembers={crowdpadItem.buyersCount || 1}
      totalNfts={crowdpadItem?.boxCount || crowdpadItem.target?.boxItemCount || 1}
      network={crowdpadItem.chainId}
      imageUrl={String(crowdpadItem.target.metadata.image)}
      urlRedirect={`/crowdpad/${crowdpadItem.chainId}/detail/${crowdpadItem.id}`}
    >
      <Footer>
        <Info>
          <h4>{`${crowdpadItem?.target.metadata.name || `${crowdpadItem?.target.collection.name} #${crowdpadItem?.target.tokenId}`} `}</h4>
          <div>
            <h2>{nftPriceType?.title}</h2>
            <div>
              <Icon name={networkNameById(crowdpadItem.chainId)} size={12} />
              <span>{nftPriceType?.value}</span>
              <small>{nftPriceType?.symbol}</small>
            </div>
          </div>
        </Info>
        {nftPriceType && (
          <StatusBar>
            <div>
              <Icon name={nftPriceType?.icon} size={18} />
              <span>{nftPriceType?.statusBar}</span>
            </div>
            <div>
              {nftPriceType.type === 'poolProgress' && (
                <ProgressBar>
                  <Progress showInfo percent={Number(currentPoolProgress)} status={currentPoolProgress === '100' ? 'success' : 'normal'} />
                </ProgressBar>
              )}
              {nftPriceType.type === 'liveAuction' && (
                <Countdown>
                  <CountdownFooterCard cutoff={Number(crowdpadItem.cutoff)} />
                </Countdown>
              )}
            </div>
          </StatusBar>
        )}
      </Footer>
    </CardBase>
  )
}

const { Footer, Info, StatusBar, Countdown, ProgressBar } = {
  Footer: styled.div`
    background: ${props => props.theme.colors.gray[1]};
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;
  `,
  Info: styled.div`
    padding: 8px 16px;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 28px 24px auto;

    > h4 {
      font-style: normal;
      font-weight: 500;
      font-size: 16px;
      line-height: normal;
      color: ${props => props.theme.colors.black};
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }
    > div {
      display: grid;
      gap: 4px;
      grid-template-columns: 1fr 1fr;

      margin-bottom: 8px;

      > h2 {
        font-style: normal;
        font-weight: 400;
        font-size: 12px;
        line-height: 133%;
        color: ${props => props.theme.colors.black};
      }
      > div {
        display: grid;
        gap: 4px;
        grid-template-columns: 18px auto auto;
        justify-content: flex-end;

        > span {
          font-weight: 500;
          font-size: 16px;
          line-height: 100%;
          color: ${props => props.theme.colors.black};

          width: auto;
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
          width: 100%;
          display: block;
          margin: 0 4px;
        }

        > small {
          font-style: normal;
          font-weight: 400;
          font-size: 12px;
          line-height: 133%;
          color: ${props => props.theme.colors.black};
        }
      }
    }
  `,
  StatusBar: styled.div`
    display: grid;
    grid-template-columns: 1fr 120px;
    gap: 8px;
    align-items: center;
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;
    padding-left: 16px;
    padding-right: 16px;
    height: 56px;
    background: ${props => props.theme.colors.gray[0]};

    > div:nth-child(1) {
      display: grid;
      align-items: center;
      grid-template-columns: 14px 1fr;
      gap: 8px;
      > span {
        font-size: 14px;
        line-height: 133%;
        font-weight: 400;
        font-style: normal;
        color: ${props => props.theme.colors.black};
      }
    }
  `,
  Countdown: styled.div`
    > span {
      font-weight: 500;
      font-size: 14px;
      color: ${props => props.theme.colors.black};
    }
  `,
  ProgressBar: styled.div`
    .ant-progress-line {
      display: grid;
      grid-template-columns: 1fr auto;
      align-items: center;
      justify-content: flex-end;
    }

    .ant-progress-outer {
      display: grid;
      align-items: center;
      margin-right: 0;
      padding-right: 0;
    }

    .ant-progress.ant-progress-line.ant-progress-status-normal.ant-progress-show-info.ant-progress-default {
      span {
        font-style: normal;
        font-weight: 500;
        font-size: 13px;
        line-height: 33px;
        margin-left: 8px;
        margin-right: 8px;
      }
    }

    .ant-progress-inner {
      background: ${props => props.theme.colors.white};
      border: 1px solid ${props => props.theme.colors.gray[0]};
      margin-top: 2px;
    }

    .ant-progress-text {
      color: ${props => props.theme.colors.black};
      font-size: 16px;
      line-height: 16px;
      font-weight: 500;
      text-align: right;
      margin-left: 8px !important;
      margin-right: 0 !important;
      width: auto;
    }

    .ant-progress-bg {
      height: 8px !important;
    }
  `
}
